import React, { useEffect, useState } from "react";
import MarketingLayout from "@/components/MarketingLayout";
import Seo, { FaqJsonLd } from "@/components/Seo";
import Markdown from "@/components/Markdown";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";
import { toast } from "sonner";

export function LegalPage({ slug }) {
  const [page, setPage] = useState(null);
  useEffect(() => { api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage(false)); }, [slug]);
  return (
    <MarketingLayout>
      <Seo title={page?.title || slug} path={`/${slug === "about" ? "about" : slug}`} description={`${page?.title || slug} for BINAREX.CO`} />
      <article className="max-w-3xl mx-auto px-5 py-14">
        {page === null && <div className="text-slate-400 text-center py-12">Loading…</div>}
        {page === false && <div className="text-slate-400 text-center py-12">Page not found.</div>}
        {page && page !== false && (
          <>
            <h1 className="bx-heading text-3xl font-extrabold">{page.title}</h1>
            <div className="mt-6"><Markdown>{page.content}</Markdown></div>
          </>
        )}
      </article>
    </MarketingLayout>
  );
}

export function FAQPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState({});
  useEffect(() => { api.get("/faq").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const grouped = items.reduce((acc, x) => { (acc[x.category] = acc[x.category] || []).push(x); return acc; }, {});
  return (
    <MarketingLayout>
      <Seo title="FAQ" path="/faq" description="Frequently asked questions about BINAREX.CO, credits, AI tools, billing, and security." />
      <FaqJsonLd items={items.map((x) => ({ q: x.question, a: x.answer }))} />
      <section className="max-w-3xl mx-auto px-5 py-14">
        <div className="bx-chip"><Lucide.HelpCircle size={12} /> FREQUENTLY ASKED</div>
        <h1 className="bx-heading mt-3 text-3xl font-extrabold">Got questions? We have answers.</h1>
        <p className="text-slate-400 mt-2">Everything you need to know about BINAREX.CO and our credit system.</p>
        <div className="mt-8 space-y-6">
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat}>
              <div className="text-xs uppercase tracking-[0.2em] text-purple-300 mb-2">{cat}</div>
              <div className="space-y-2">
                {list.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setOpen((o) => ({ ...o, [f.id]: !o[f.id] }))}
                    data-testid={`faq-${f.id}`}
                    className="bx-card w-full text-left p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="font-semibold">{f.question}</div>
                      <Lucide.ChevronDown size={18} className={`transition ${open[f.id] ? "rotate-180" : ""}`} />
                    </div>
                    {open[f.id] && <div className="text-sm text-slate-300 mt-3">{f.answer}</div>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}

export function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll get back within 1 business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Could not send message");
    } finally { setBusy(false); }
  };
  return (
    <MarketingLayout>
      <Seo title="Contact" path="/contact" description="Contact BINAREX AND CO — questions, partnerships, support. We reply within 1 business day." />
      <section className="max-w-2xl mx-auto px-5 py-14">
        <div className="bx-chip"><Lucide.Mail size={12} /> CONTACT US</div>
        <h1 className="bx-heading mt-3 text-3xl font-extrabold">Let's talk.</h1>
        <p className="text-slate-400 mt-2">Have a question, a partnership idea, or feedback? Drop us a line.</p>
        <form onSubmit={submit} className="bx-card p-6 mt-8 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-slate-400">Name</label>
              <input data-testid="contact-name" required value={form.name} onChange={set("name")} className="bx-input mt-1" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Email</label>
              <input data-testid="contact-email" required type="email" value={form.email} onChange={set("email")} className="bx-input mt-1" />
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400">Subject</label>
            <input data-testid="contact-subject" required value={form.subject} onChange={set("subject")} className="bx-input mt-1" />
          </div>
          <div>
            <label className="text-xs text-slate-400">Message</label>
            <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className="bx-input mt-1 resize-y" />
          </div>
          <button data-testid="contact-submit" disabled={busy} className="bx-btn-primary w-full justify-center">
            {busy ? <Lucide.Loader2 className="animate-spin" size={16} /> : <Lucide.Send size={16} />} Send Message
          </button>
        </form>
        <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
          {[
            ["Mail", "Email", "hello@binarex.co"],
            ["LifeBuoy", "Support", "support@binarex.co"],
            ["Building2", "Company", "BINAREX AND CO"],
          ].map(([i, t, d]) => {
            const Cmp = Lucide[i];
            return (
              <div key={t} className="bx-card p-4">
                <span className="bx-glow-icon"><Cmp size={16} /></span>
                <div className="mt-3 text-xs text-slate-400">{t}</div>
                <div className="text-sm">{d}</div>
              </div>
            );
          })}
        </div>
      </section>
    </MarketingLayout>
  );
}
